/**
 * Account Setup Form Component
 *
 * Standalone form for completing account setup.
 * Used by the Account Setup page for users coming from setup email links.
 *
 * @module pages/auth/components
 */

import { useState, useEffect, useRef } from 'react';
import type { FormEvent } from 'react';
import { isValidPhone, normalizePhone, phoneErrorMessage, type PhoneRegion } from '@butter/library/phone';
import { EyeIcon } from '@butter/ui/icons';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';
import {
  validateUserPassword,
  validatePasswordMatch
} from '~/lib/utils/passwordValidation';
import { isPlaceholderEmail } from '@butter/library/shared';
import { isPlaceholderName } from '@butter/library/shared';
import { useAuth } from '~/services/auth';
import type { WaiverConsent } from '~/services/internalApi/waiver';

export interface AccountSetupFormProps {
  /** User ID of the account being set up */
  userId: string | number;
  /** User's email (pre-filled, read-only) */
  email: string;
  /** Pre-filled first name from user data */
  initialFirstName?: string;
  /** Pre-filled last name from user data */
  initialLastName?: string;
  /** Pre-filled phone number from user data */
  initialPhone?: string;
  /**
   * Callback when account setup is complete. `signInRequired` when the account kept an
   * existing Butter login: no session was created, so the member signs in themselves.
   */
  onComplete: (result: { signInRequired: boolean }) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
  /** Optional club ID */
  clubId?: string | number;
  /** The club's country — how a number typed without a country code is read. Defaults to US. */
  phoneRegion?: PhoneRegion;
  /** Setup token from email link (for future server-side validation) */
  setupToken?: string;
  /** When true, the email field is editable (placeholder-email user must supply real email) */
  requireEmailInput?: boolean;
  /**
   * The email already has a Butter login elsewhere. The password field is disabled with a
   * hint, nothing is sent for it, and setup does not sign the member in — the server never
   * touches a login another account shares.
   */
  existingLogin?: boolean;
  /**
   * Mirrors the internal submitting state up to the page, which owns the submit button.
   * The button lives OUTSIDE this form (associated by `form="account-setup-form"`) so
   * that on narrow screens it renders BELOW the payment/confirm gates that enable it —
   * see the action bar in AccountSetupPage.
   */
  onSubmittingChange?: (isSubmitting: boolean) => void;
  /**
   * The waiver consent the PAGE captured, read at submit time and sent WITH the
   * activation request — the server writes it in the same transaction as the status
   * flip. A function rather than a value so the submit reads the latest state without
   * re-rendering this form on every stroke.
   */
  getWaiverConsents?: () => WaiverConsent[];
}

/**
 * Shared between this form and the page's action bar: the `form=` attribute that makes
 * an out-of-tree button submit this form.
 */
export const ACCOUNT_SETUP_FORM_ID = 'account-setup-form';

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

/**
 * Account Setup Form
 *
 * Collects:
 * - First Name (required, may be pre-filled)
 * - Last Name (required, may be pre-filled)
 * - Phone Number (required, may be pre-filled)
 * - Password (required, with strength indicator)
 * - Confirm Password (required)
 *
 * @example
 * ```tsx
 * <AccountSetupForm
 *   userId={user.id}
 *   email={user.email}
 *   initialFirstName={user.firstName}
 * />
 * ```
 */
export function AccountSetupForm({
  userId,
  email,
  requireEmailInput = false,
  existingLogin = false,
  initialFirstName = '',
  initialLastName = '',
  initialPhone = '',
  onComplete,
  onError,
  clubId,
  phoneRegion = 'US',
  setupToken,
  onSubmittingChange,
  getWaiverConsents,
}: AccountSetupFormProps) {
  const { login } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    firstName: isPlaceholderName(initialFirstName || '') ? '' : (initialFirstName || ''),
    lastName: isPlaceholderName(initialLastName || '') ? '' : (initialLastName || ''),
    phone: initialPhone || '',
    password: '',
    confirmPassword: '',
  });

  const [emailValue, setEmailValue] = useState(email);

  useEffect(() => {
    if (!requireEmailInput) {
      setEmailValue(email);
    }
  }, [email, requireEmailInput]);

  const [smsConsent, setSmsConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /**
   * Activation succeeded and must not be replayed — the setup token is single-use and
   * the endpoint refuses an already-active member, so a second attempt dead-ends on
   * "already set up". Tracked separately from the login below: when `login()` is the
   * call that failed, the retry has to resume at the login, not at activation.
   *
   * Refs rather than state: nothing renders from them, and a re-render between the two
   * awaits would lose a `useState` update.
   */
  const accountActivatedRef = useRef(false);
  const loggedInRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form validation
  const validateForm = (): string | null => {
    if (requireEmailInput) {
      if (!emailValue.trim()) return 'Email address is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.trim())) return 'Please enter a valid email address';
      if (isPlaceholderEmail(emailValue.trim())) return 'Please enter your real email address';
    }
    if (!formData.firstName.trim()) {
      return 'First name is required';
    }
    if (isPlaceholderName(formData.firstName.trim())) {
      return 'Please enter your real first name';
    }
    if (!formData.lastName.trim()) {
      return 'Last name is required';
    }
    if (isPlaceholderName(formData.lastName.trim())) {
      return 'Please enter your real last name';
    }
    if (!formData.phone.trim()) {
      return 'Phone number is required';
    }
    if (!isValidPhone(formData.phone, phoneRegion)) {
      return 'Please enter a valid phone number';
    }

    // An existing login keeps its password — there is nothing to validate.
    if (existingLogin) return null;

    // Password validation
    const passwordValidation = validateUserPassword(formData.password);
    if (!passwordValidation.isValid) {
      return passwordValidation.errors[0];
    }

    // Password match validation
    const matchError = validatePasswordMatch(formData.password, formData.confirmPassword);
    if (matchError) {
      return matchError;
    }

    return null;
  };

  // Handle input changes
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      onError?.(validationError);
      // The submit button lives below both columns, so on a narrow screen the error
      // banner at the top of this form can be a full viewport away from the control
      // that was just tapped. Without this the layout fix would just move the bug.
      document.getElementById(ACCOUNT_SETUP_FORM_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    try {
      setIsLoading(true);
      onSubmittingChange?.(true);
      setError(null);

      // Import auth service
      const { authService } = await import('~/services/auth');

      // Validated against the same region above, so this cannot fail in practice.
      const normalizedPhone = normalizePhone(formData.phone, phoneRegion);
      if (!normalizedPhone.ok) throw new Error(phoneErrorMessage(normalizedPhone.reason));

      if (!accountActivatedRef.current) {
        // Complete account setup. The waiver consent rides along: the server records it
        // in the same transaction as activation, so there is no window in which the
        // account exists and the signature does not.
        await authService.completeAccountSetup(
          userId,
          {
            email: emailValue.trim(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            phone: normalizedPhone.e164,
            ...(existingLogin ? {} : { password: formData.password }),
            // Consent is to a number: a box ticked before the phone was cleared never sends.
            smsConsent: smsConsent && formData.phone.trim() !== '',
            waiverConsents: getWaiverConsents?.(),
          },
          clubId,
          setupToken // Pass token for server-side validation
        );

        accountActivatedRef.current = true;
      }

      // An existing login: setup created no session and we hold no password to make one.
      if (existingLogin) {
        onComplete({ signInRequired: true });
        return;
      }

      if (!loggedInRef.current) {
        // Establish the session so the user is authenticated after setup (mirrors the
        // booking CompleteAccountForm) — setup returns a session, but the React auth
        // context is only populated by going through login().
        await login(emailValue.trim(), formData.password);

        loggedInRef.current = true;
      }

      // Success - call parent callback
      onComplete({ signInRequired: false });
    } catch (err) {
      console.error('[AccountSetupForm] Setup failed:', err);
      const errorMessage = err instanceof Error
        ? err.message
        : 'Failed to complete account setup. Please try again.';
      setError(errorMessage);
      onError?.(errorMessage);
      document.getElementById(ACCOUNT_SETUP_FORM_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setIsLoading(false);
      onSubmittingChange?.(false);
    }
  };

  return (
    <form id={ACCOUNT_SETUP_FORM_ID} onSubmit={handleSubmit} className="space-y-4">
      {/* Error Message */}
      {error && (
        <div
          className="p-4 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
          aria-live="polite"
        >
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Email */}
      <div className="text-sm">
        <label
          htmlFor="email"
          className="block font-medium text-fg-quaternary mb-2"
        >
          Email Address {requireEmailInput && <span className="text-red-500" aria-hidden="true">*</span>}
        </label>
        {requireEmailInput ? (
          <input
            id="email"
            type="email"
            value={emailValue}
            onChange={(e) => { setEmailValue(e.target.value); if (error) setError(null); }}
            disabled={isLoading}
            required
            placeholder="your@email.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50"
          />
        ) : (
          <input
            id="email"
            type="email"
            value={emailValue}
            disabled
            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-fg-quaternary cursor-not-allowed"
          />
        )}
      </div>

      {/* First Name */}
      <div className="text-sm">
        <label
          htmlFor="firstName"
          className="block font-medium text-fg-quaternary mb-2"
        >
          First Name <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="firstName"
          type="text"
          value={formData.firstName}
          onChange={(e) => handleChange('firstName', e.target.value)}
          disabled={isLoading}
          required
          placeholder="John"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50"
        />
      </div>

      {/* Last Name */}
      <div className="text-sm">
        <label
          htmlFor="lastName"
          className="block font-medium text-fg-quaternary mb-2"
        >
          Last Name <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="lastName"
          type="text"
          value={formData.lastName}
          onChange={(e) => handleChange('lastName', e.target.value)}
          disabled={isLoading}
          required
          placeholder="Doe"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50"
        />
      </div>

      {/* Phone Number */}
      <div className="text-sm">
        <label
          htmlFor="phone"
          className="block font-medium text-fg-quaternary mb-2"
        >
          Phone Number <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          disabled={isLoading}
          required
          placeholder="+1 (555) 123-4567"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50"
        />
      </div>

      {/* SMS & Email Consent */}
      <div className="flex items-start gap-3 pt-2">
        <input
          id="sms-consent"
          type="checkbox"
          checked={smsConsent && formData.phone.trim() !== ''}
          onChange={(e) => setSmsConsent(e.target.checked)}
          // Nothing to consent at until there is a number.
          disabled={isLoading || formData.phone.trim() === ''}
          className="mt-0.5 h-4 w-4 text-theme-600 border-gray-300 rounded focus:ring-theme-600 flex-shrink-0"
        />
        <label htmlFor="sms-consent" className="text-xs text-fg-quaternary leading-relaxed">
          I agree to receive text messages and emails from{' '}
          <span className="font-medium text-fg-primary">Butter Software</span>{' '}
          at the phone number and email address provided above. Messages may include booking
          confirmations, tee time reminders, and account notifications.{' '}
          <span className="font-medium text-fg-primary">Message frequency varies. Msg &amp; data rates may apply.</span>{' '}
          Reply <span className="font-medium">STOP</span> to unsubscribe &middot; Reply{' '}
          <span className="font-medium">HELP</span> for help.{' '}
          <a
            href="https://buttersoftware.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme-600 underline text-interactive:text-theme-700"
          >
            Privacy Policy
          </a>
          {' '}&middot;{' '}
          <a
            href="https://buttersoftware.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme-600 underline text-interactive:text-theme-700"
          >
            Terms of Service
          </a>
        </label>
      </div>

      {existingLogin ? (
        // The email already has a Butter login — its password stays as it is. Disabled rather
        // than hidden, so the member sees where a password would go and why there isn't one.
        <div className="text-sm">
          <label
            htmlFor="password"
            className="block font-medium text-fg-quaternary mb-2"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            value=""
            disabled
            placeholder="Your existing Butter password"
            aria-describedby="existing-login-hint"
            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-fg-quaternary cursor-not-allowed"
          />
          <p id="existing-login-hint" className="mt-2 text-xs text-fg-quaternary">
            You already have a Butter login with this email. Use your existing Butter password —
            you&apos;ll sign in with it once setup is complete.
          </p>
        </div>
      ) : (
        <>
        {/* Password */}
        <div className="text-sm">
          <label
            htmlFor="password"
            className="block font-medium text-fg-quaternary mb-2"
          >
            Password <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              disabled={isLoading}
              required
              minLength={8}
              placeholder="Min. 8 characters"
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon
                isOpen={showPassword}
                className="size-6 text-gray-500"
                ariaHidden={true}
              />
            </button>
          </div>

          {/* Password Strength Indicator */}
          <PasswordStrengthIndicator password={formData.password} />
        </div>

        {/* Confirm Password */}
        <div className="text-sm">
          <label
            htmlFor="confirmPassword"
            className="block font-medium text-fg-quaternary mb-2"
          >
            Confirm Password <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              disabled={isLoading}
              required
              placeholder="Re-enter password"
              className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-theme-600 focus:border-transparent disabled:bg-gray-50"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon
                isOpen={showConfirmPassword}
                className="size-6 text-gray-500"
                ariaHidden={true}
              />
            </button>
          </div>
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="mt-1 text-xs text-red-600">
              Passwords do not match
            </p>
          )}
        </div>
        </>
      )}

      {/*
        No submit button here on purpose. It lives in AccountSetupPage's action bar
        below both columns, associated back to this form by `form={ACCOUNT_SETUP_FORM_ID}`.
        That keeps the gates (payment, confirm-details) ABOVE the button on narrow
        screens, where the two-column grid collapses.

        An out-of-tree submit button is still this form's default button, so native
        constraint validation and implicit submission (Enter in a text field) both keep
        working — and stay correctly suppressed while that button is disabled.
      */}
    </form>
  );
}
